import React, { useRef } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import { Card, CardContent, Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import Fab from '@mui/material/Fab';
import ListItemText from '@mui/material/ListItemText';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { fieldFullName } from '../../VisitorList/components/ImportXLS/import_utils';
import { SystemFieldType, FieldType } from '@/api';

const PREFIX = 'FieldsTable';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  tableRow: `${PREFIX}-tableRow`,
  firstCol: `${PREFIX}-firstCol`,
  lastCol: `${PREFIX}-lastCol`,
  inner: `${PREFIX}-inner`,
  nameContainer: `${PREFIX}-nameContainer`,
  avatar: `${PREFIX}-avatar`,
  actions: `${PREFIX}-actions`,
};

const StyledDragDropContext = styled(DragDropContext)(({ theme }) => ({
  [`& .${classes.root}`]: {
    overflowX: 'auto',
  },

  [`& .${classes.content}`]: {
    padding: 0,
  },

  [`& .${classes.tableRow}`]: {
    background: 'white',
  },

  [`& .${classes.firstCol}`]: {
    width: 63,
  },

  [`& .${classes.lastCol}`]: {
    width: 200,
  },

  [`& .${classes.inner}`]: {
    minWidth: 600,
  },

  [`& .${classes.nameContainer}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.avatar}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    justifyContent: 'flex-end',
  },
}));

const SystemFieldNames = {
  phoneNumber: 'Телефонный номер',
  email: 'E-mail',
  photo: 'Фото',
  firstName: 'Имя',
  lastName: 'Фамилия',
  middleName: 'Отчество',
  group: 'Группа',
};

const FieldsTable = props => {
  const { className, meetupId, data, updateFields, updateUniqueFields, ...rest } = props;
  const { fields, uniqueFields } = data;

  const handleDelete = idx => {
    fields.splice(idx, 1);
    updateFields(fields);
  };

  const isMandatory = field => {
    if (!field.systemField) {
      return false;
    }
    return (
      field.systemField === SystemFieldType.FirstName ||
      field.systemField === SystemFieldType.LastName ||
      field.systemField === SystemFieldType.Group
    );
  };

  const tableRef = useRef();
  const onBeforeDragStart = () => {
    const tableRows = tableRef.current.children;

    for (let tableRow of tableRows) {
      for (let tableCell of tableRow.children) {
        tableCell.style.width = getComputedStyle(tableCell).width;
      }
    }
  };

  const onDragEnd = result => {
    const { destination, source } = result;

    if (!destination || source.index === destination.index) {
      return;
    }

    const f = fields[source.index];
    fields.splice(source.index, 1);
    fields.splice(destination.index, 0, f);
    updateFields(fields);
  };

  return (
    <StyledDragDropContext onDragEnd={onDragEnd} onBeforeDragStart={onBeforeDragStart}>
      <Droppable droppableId={'fieldsTable'}>
        {provided => (
          <Card
            {...rest}
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={clsx(classes.root, className)}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.firstCol}></TableCell>
                  <TableCell>Поле</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Обязательность</TableCell>
                  <TableCell>Уникальность</TableCell>
                  <TableCell className={classes.lastCol}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody ref={tableRef}>
                {fields.map((field, idx) => (
                  <Draggable
                    draggableId={field.systemField || field.name}
                    key={field.systemField || field.name}
                    index={idx}
                  >
                    {(provided, snapshot) => {
                      return (
                        <TableRow
                          className={classes.tableRow}
                          hover
                          sx={{ height: 'auto' }}
                          {...provided.draggableProps}
                          ref={provided.innerRef}
                        >
                          <TableCell {...provided.dragHandleProps}>
                            <DragIndicatorIcon fontSize='large' color='disabled' />
                          </TableCell>
                          <TableCell>
                            <ListItemText primary={field.label} secondary={fieldFullName(field)} />
                          </TableCell>
                          <TableCell>
                            <ListItemText
                              primary={
                                SystemFieldNames[field.systemField] ||
                                (field.type === FieldType.Transliteration ? 'Транслитерация' : 'Текстовое поле')
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              checked={fields[idx].required}
                              onChange={e => {
                                fields[idx].required = !fields[idx].required;
                                updateFields(fields);
                              }}
                              disabled={isMandatory(field)}
                            />
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              checked={uniqueFields && uniqueFields.indexOf(fieldFullName(field)) !== -1}
                              onChange={e => updateUniqueFields(fieldFullName(field), e.target.checked)}
                              disabled={field.systemField === SystemFieldType.Group}
                            />
                          </TableCell>
                          <TableCell className={classes.lastCol}>
                            <RouterLink to={'/m/' + meetupId + '/settings/field/i/' + idx}>
                              <Fab size='small' color='secondary' aria-label='edit' className={classes.fab}>
                                <EditIcon />
                              </Fab>
                            </RouterLink>

                            <Fab
                              size='small'
                              color='secondary'
                              aria-label='delete'
                              className={classes.fab}
                              disabled={isMandatory(field)}
                              onClick={() => handleDelete(idx)}
                            >
                              <DeleteIcon />
                            </Fab>
                          </TableCell>
                        </TableRow>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            </Table>
          </Card>
        )}
      </Droppable>
    </StyledDragDropContext>
  );
};

FieldsTable.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object,
  updateFields: PropTypes.func,
  updateUniqueFields: PropTypes.func,
};

export default FieldsTable;
